import type {
  CompanyDiscoveryAiOutput,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import type { GlobalAssistantReportParamSuggestion } from './global-ai-assistant-api';

export type ChartDataDigest = {
  kind: 'chart_data';
  title?: string;
  metric: string;
  chartType: string;
  rowCount: number;
  latestValue?: number;
  /** Earliest value of the primary series (pairs with `firstDate`). */
  firstValue?: number;
  /**
   * Highest value of the primary series and the date it occurred. Lets narration
   * anchor on the VISIBLE peak ("down from a 50% peak in 2019") rather than the
   * series' first point, which for rate metrics (hiring/attrition/growth) is
   * often an unrepresentative early spike that `deltaPct` over-weights.
   */
  peakValue?: number;
  peakDate?: string;
  /** Whole-series change (first→last). Can mislead when the first point is an early spike — pair it with the actual endpoint values when narrating. */
  deltaPct?: number;
  /** First/last date of the primary series, when time-based. */
  firstDate?: string;
  lastDate?: string;
  topCategories?: Array<{ label: string; value: number }>;
  /**
   * Ranked WORST end of a categorical breakdown (bottom-5, worst first) —
   * only emitted when the breakdown has more rows than `topCategories`
   * covers. For `sentiment_top_bottom` this is the attrition-factor side:
   * the lowest-rated aspects a "why are people leaving" answer cites as its
   * grounded driver signals.
   */
  bottomCategories?: Array<{ label: string; value: number }>;
  empty: boolean;
  /**
   * Labels of the demographic/entity filters the chart was ACTUALLY rendered
   * with (e.g. `["company: Google LLC", "ethnicity", "gender"]`). An EXPLICIT,
   * structured record that those filters resolved + applied, so the grounded
   * narration cannot infer "unfiltered" from `rowCount: 1` + a bare metric and
   * invent "the filter did not resolve" — the title alone (a description)
   * wasn't trusted against a single-row result (FE-2117).
   */
  filtersApplied?: string[];
  /**
   * The RESOLVED display label(s) of the applied role filter (from the chart
   * scope's labels-patched `activeFilterMap`, i.e. the same names the filter
   * chips render). The AI response carries only taxonomy-relative numeric role
   * ids, so `filtersApplied` can state a bare `role` at best — this field is
   * what lets the narration name the role the backend ACTUALLY resolved
   * ("Aircraft Mechanics") instead of parroting the user's own term
   * ("nurses") when resolution landed somewhere else.
   */
  resolvedRoleLabel?: string;
  /**
   * Set when the chart settled in an ERROR phase (data layer failed to load).
   * Distinguishes a render/data FAILURE from a genuinely-empty result so the
   * model narrates "couldn't load that chart" instead of "no data" (FE-2117).
   */
  error?: true;
};

export type FilterResolutionDigest = {
  kind: 'filter_resolution';
  applied: Array<{ requested: string; resolvedTo: string; filterId: string }>;
  unresolved: Array<{ requested: string; reason?: string }>;
  targetPage: string;
};

export type ReportPrefillDigest = {
  kind: 'report_prefill';
  reportName: string;
  resolved: Array<{ parameter: string; value: string }>;
  skipped: Array<{ parameter: string; searchTerm: string; reason: string }>;
};

type Row = Record<string, unknown>;

function isRow(value: unknown): value is Row {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function rowArray(value: unknown): Row[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRow);
}

/**
 * What the digest works from after unwrapping the provider payload:
 * `rows` carries the primary series (time series points or categorical
 * entries); `seriesLatest` carries one latest-value entry per series when
 * the payload holds several (multi-series timeseries / breakdowns).
 */
type ChartShape = {
  rows: Row[];
  seriesLatest?: Array<{ label: string; value: number }>;
};

function lastNumericValue(points: Row[]): number | undefined {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const value = points[i]?.value;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

function dimLabel(item: Row): string {
  const candidate = item.dim ?? item.longName ?? item.id;
  return typeof candidate === 'string' ? candidate : 'unknown';
}

/**
 * Unwrap the chart data the FE providers actually emit. CustomDash
 * `DataProviderSet`s are envelope objects (`{ type, data: [...] }`) whose
 * items are timeseries (`{dim, timeseries: [{date, value}]}`), single
 * points (`{dim, value}`), breakdowns (`{dim, breakdown: [...]}`) or
 * distributions (`{dim, distribution: [...]}`). Plain row arrays are kept
 * for tool-result / test inputs. Anything else digests as empty.
 */
function isMultiMetricResult(
  data: unknown
): data is { byMetric: Record<string, unknown>; fallback?: unknown } {
  return isRow(data) && isRow((data as Row).byMetric);
}

function toChartShape(data: unknown, metric?: string): ChartShape {
  // Multi-metric providers (table charts always; gauge / y-bounded line via
  // `needsMultiMetricData`) emit `{ byMetric: Record<metric, DataProviderSet>,
  // fallback?: DataProviderSet }`, NOT the bare `{ type, data: [...] }`
  // envelope. Unwrap to the active metric's DataProviderSet (else `fallback`,
  // else the first populated metric) before the envelope checks below —
  // otherwise a widget that rendered real data digests as empty and the model
  // narrates "no data" for a chart that has data (FE-2117).
  if (isMultiMetricResult(data)) {
    const { byMetric } = data;
    const selected =
      (metric !== undefined ? byMetric[metric] : undefined) ??
      data.fallback ??
      Object.values(byMetric).find((set) => set !== undefined);
    return toChartShape(selected);
  }
  if (Array.isArray(data)) {
    return { rows: rowArray(data) };
  }
  if (!isRow(data) || !Array.isArray(data.data)) {
    return { rows: [] };
  }
  const items = rowArray(data.data);
  if (items.length === 0) return { rows: [] };

  const first = items[0];
  if (first && Array.isArray(first.timeseries)) {
    const primary =
      items.find((item) => item.seriesType === 'primary') ?? first;
    const rows = rowArray(primary.timeseries);
    if (items.length === 1) return { rows };
    const seriesLatest = items
      .map((item) => {
        const latest = lastNumericValue(rowArray(item.timeseries));
        return latest === undefined
          ? undefined
          : { label: dimLabel(item), value: latest };
      })
      .filter((entry): entry is { label: string; value: number } =>
        Boolean(entry)
      );
    return { rows, seriesLatest };
  }
  if (first && Array.isArray(first.breakdown)) {
    return { rows: rowArray(first.breakdown) };
  }
  if (first && Array.isArray(first.distribution)) {
    return { rows: rowArray(first.distribution) };
  }
  // SingleDataPoint items ({dim, value}) — categorical.
  return { rows: items };
}

function numericValues(rows: Row[]): number[] {
  // The single numeric series: prefer a `value` key; otherwise the first
  // numeric field found per row.
  return rows
    .map((row) => {
      if (typeof row.value === 'number') return row.value;
      const firstNumeric = Object.values(row).find(
        (v): v is number => typeof v === 'number'
      );
      return firstNumeric;
    })
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
}

function labelOf(row: Row): string | undefined {
  const candidate =
    row.label ?? row.dim ?? row.name ?? row.category ?? row.date;
  return typeof candidate === 'string' ? candidate : undefined;
}

function isTimeSeries(rows: Row[]): boolean {
  return rows.some((row) => typeof row.date === 'string');
}

/** Round to a narration-friendly precision (no fractional headcounts). */
function roundForNarration(value: number | undefined): number | undefined {
  if (value === undefined) return undefined;
  return Math.abs(value) >= 100
    ? Math.round(value)
    : Math.round(value * 100) / 100;
}

export function buildChartDataDigest(params: {
  data: unknown;
  metric: string;
  chartType: string;
  title?: string;
}): ChartDataDigest {
  const { rows, seriesLatest } = toChartShape(params.data, params.metric);
  const values = numericValues(rows);
  const empty = rows.length === 0 || values.length === 0;

  let latestValue: number | undefined;
  let firstValue: number | undefined;
  let peakValue: number | undefined;
  let peakDate: string | undefined;
  let deltaPct: number | undefined;
  let firstDate: string | undefined;
  let lastDate: string | undefined;
  let topCategories: Array<{ label: string; value: number }> | undefined;
  let bottomCategories: Array<{ label: string; value: number }> | undefined;

  if (!empty) {
    if (isTimeSeries(rows)) {
      // Derive latest/first from the same rows that carry a finite value, so
      // `latestValue` and `lastDate` describe the SAME (last complete) data
      // point. Taking `lastDate` from the raw `dates` array would point at a
      // trailing incomplete period whose value is null, mismatching the last
      // finite `latestValue` (FE-2117).
      const finiteRows = rows.filter(
        (row) =>
          typeof row.value === 'number' && Number.isFinite(row.value as number)
      );
      const lastCompleteRow = finiteRows[finiteRows.length - 1];
      const firstCompleteRow = finiteRows[0];
      const rawLatest =
        typeof lastCompleteRow?.value === 'number'
          ? lastCompleteRow.value
          : undefined;
      const rawFirst =
        typeof firstCompleteRow?.value === 'number'
          ? firstCompleteRow.value
          : undefined;
      latestValue = roundForNarration(rawLatest);
      firstValue = roundForNarration(rawFirst);
      // Peak of the primary series, so narration can anchor on the visible high
      // point rather than the (often spiky) first point.
      let peakRow = finiteRows[0];
      for (const row of finiteRows) {
        if (
          typeof row.value === 'number' &&
          (peakRow === undefined ||
            typeof peakRow.value !== 'number' ||
            row.value > peakRow.value)
        ) {
          peakRow = row;
        }
      }
      if (peakRow && typeof peakRow.value === 'number') {
        peakValue = roundForNarration(peakRow.value);
        peakDate = typeof peakRow.date === 'string' ? peakRow.date : undefined;
      }
      // Compute the delta from the RAW first/latest values; feeding the
      // already-rounded `latestValue` in here would compound rounding error
      // (FE-2117).
      if (rawFirst !== undefined && rawFirst !== 0 && rawLatest !== undefined) {
        deltaPct =
          Math.round(((rawLatest - rawFirst) / Math.abs(rawFirst)) * 1000) / 10;
      }
      firstDate =
        typeof firstCompleteRow?.date === 'string'
          ? firstCompleteRow.date
          : undefined;
      lastDate =
        typeof lastCompleteRow?.date === 'string'
          ? lastCompleteRow.date
          : undefined;
      if (seriesLatest && seriesLatest.length > 1) {
        topCategories = [...seriesLatest]
          .sort((a, b) => b.value - a.value)
          .slice(0, 5)
          .map((entry) => ({
            label: entry.label,
            value: roundForNarration(entry.value) ?? entry.value,
          }));
      }
    } else {
      const labeled = rows
        .map((row) => {
          const label = labelOf(row);
          const rawValue =
            typeof row.value === 'number' ? row.value : numericValues([row])[0];
          // Round like every other narrated figure — raw FP arithmetic leaks
          // "-0.41000000000000003" straight into the user-facing answer (the
          // model quotes digest values verbatim, seen live on prod 2026-07-08).
          const value = roundForNarration(rawValue);
          return label !== undefined && value !== undefined
            ? { label, value }
            : undefined;
        })
        .filter((entry): entry is { label: string; value: number } =>
          Boolean(entry)
        )
        .sort((a, b) => b.value - a.value);
      if (labeled.length > 0) {
        topCategories = labeled.slice(0, 5);
        latestValue = labeled[0]?.value;
        // Worst end (worst first), only when it adds rows topCategories
        // doesn't already show — a breakdown answer cites the salient end
        // (best or worst) depending on the question, so both must be present
        // for wide breakdowns like the 20-aspect sentiment_top_bottom set.
        if (labeled.length > 5) {
          bottomCategories = labeled.slice(-5).reverse();
        }
      }
    }
  }

  return {
    kind: 'chart_data',
    title: params.title,
    metric: params.metric,
    chartType: params.chartType,
    rowCount: rows.length,
    latestValue,
    firstValue,
    peakValue,
    peakDate,
    deltaPct,
    firstDate,
    lastDate,
    topCategories,
    bottomCategories,
    empty,
  };
}

/**
 * The resolved talent-discovery dimensions other than company/school live ONLY
 * inside `filters.expr` (a `UserNode` expression tree) — the final
 * `TalentDiscoveryAiFilterSearchResponseV2` does NOT keep per-dimension named
 * lists for gender/geography/role/etc. Each leaf value carries a `kind` enum
 * (`UserCategoryKind` / `PositionCategoryKind`) identifying its dimension but
 * only id-only values (no human name). This maps each `kind` to the dimension
 * `filterId` we narrate. `COMPANY_ID` / `RSID` are intentionally excluded —
 * those are already reported from `companyNames` / `schoolNames`.
 */
const EXPR_KIND_TO_FILTER_ID: Record<string, string> = {
  // UserCategoryKind
  GENDER: 'gender',
  ETHNICITY: 'ethnicity',
  HIGHEST_DEGREE: 'education',
  SKILL_LVL_1: 'skill',
  SKILL_LVL_2: 'skill',
  SKILL_LVL_3: 'skill',
  SKILL_LVL_4: 'skill',
  SKILL_LVL_5: 'skill',
  USER_CITY: 'geography',
  USER_COUNTRY: 'geography',
  USER_MSA: 'geography',
  USER_REGION: 'geography',
  USER_STATE: 'geography',
  // PositionCategoryKind
  CITY: 'geography',
  COUNTRY: 'geography',
  MSA: 'geography',
  REGION: 'geography',
  CONTINGENT: 'contingent',
  FLIGHT_RISK: 'flight_risk',
  PRESTIGE: 'prestige',
  REMOTE_SUITABILITY: 'remote_suitability',
  EMPLOYMENT_RELATIONSHIP: 'employment_relationship',
  WORK_SCHEDULE: 'work_schedule',
  RICS_K10: 'industry',
  RICS_K50: 'industry',
  RICS_K400: 'industry',
  ROLE_K7: 'role',
  ROLE_K150: 'role',
  ROLE_K1500: 'role',
  ROLE_K15000: 'role',
  ROLE_K17000: 'role',
  SENIORITY: 'seniority',
  // PositionTextKind / range kinds
  TITLE: 'role',
  SALARY_BASE: 'salary',
  SALARY_TOTAL: 'salary',
  GRADUATION_YEAR: 'graduation_year',
  YEARS_OF_EXP: 'years_of_experience',
};

/**
 * Walk a `UserNode` expr tree and collect the set of dimension `filterId`s
 * present, in first-seen order. Company/school kinds are skipped (reported
 * separately from the named lists). Everything is read defensively as
 * `unknown` because the tree is deeply nested and any branch may be absent.
 */
function collectExprFilterIds(expr: unknown): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();

  const recordKind = (kind: unknown): void => {
    if (typeof kind !== 'string') return;
    const filterId = EXPR_KIND_TO_FILTER_ID[kind];
    if (filterId && !seen.has(filterId)) {
      seen.add(filterId);
      ordered.push(filterId);
    }
  };

  const visitExprNode = (node: unknown): void => {
    if (!isRow(node)) return;
    if (isRow(node.and)) visitExprNode((node.and as Row).child ?? node.and);
    if (isRow(node.not)) visitExprNode((node.not as Row).child);
    if (isRow(node.or)) {
      for (const child of rowArray((node.or as Row).children)) {
        visitExprNode(child);
      }
    }
    for (const leafKey of ['val', 'rng', 'txt'] as const) {
      const leaf = node[leafKey];
      if (!isRow(leaf)) continue;
      for (const attr of Object.values(leaf)) {
        if (isRow(attr)) recordKind(attr.kind);
      }
    }
    // ExprAndNode uses `children`; ExprOrNode uses `children`; some shapes
    // nest the boolean group's children directly on the node.
    for (const child of rowArray(node.children)) {
      visitExprNode(child);
    }
  };

  const visitUserNode = (node: unknown): void => {
    if (!isRow(node)) return;
    if (isRow(node.user_and)) {
      for (const child of rowArray((node.user_and as Row).children)) {
        visitUserNode(child);
      }
    }
    if (isRow(node.user_or)) {
      for (const child of rowArray((node.user_or as Row).children)) {
        visitUserNode(child);
      }
    }
    if (isRow(node.user_not)) visitUserNode((node.user_not as Row).child);
    if (isRow(node.user_value)) {
      visitExprNode((node.user_value as Row).child);
    }
  };

  visitUserNode(expr);
  return ordered;
}

export function buildFilterResolutionDigest(params: {
  response: TalentDiscoveryAiFilterSearchResponseV2;
  targetPage: string;
}): FilterResolutionDigest {
  const { response, targetPage } = params;
  const applied: FilterResolutionDigest['applied'] = [];

  for (const company of response.filters.companyNames ?? []) {
    applied.push({
      // Resolution requests don't echo per-entity request text; pair the
      // resolved name with the valid prompt fragment when there is exactly
      // one, otherwise fall back to the resolved name itself.
      requested:
        response.relevantPromptText.valid.length === 1
          ? (response.relevantPromptText.valid[0] ?? company.name)
          : company.name,
      resolvedTo: company.name,
      filterId: 'company',
    });
  }
  for (const school of response.filters.schoolNames ?? []) {
    applied.push({
      requested: school.name,
      resolvedTo: school.name,
      filterId: 'school',
    });
  }

  // Non-company/school dimensions (gender, geography, role, seniority, …)
  // resolve into the expr tree with no per-dimension human name on the V2
  // response. Report each DISTINCT dimension once. We deliberately do NOT pair
  // the prompt fragments (`relevantPromptText.valid`) to dimensions positionally
  // — the fragment count rarely matches the dimension count, so index-pairing
  // mislabeled values (e.g. reported "California" as the gender filter, making
  // the model claim a filter "resolved twice"). The dimension list is accurate;
  // the model has the user's prompt for the specific values.
  for (const filterId of collectExprFilterIds(response.filters.expr)) {
    applied.push({ requested: filterId, resolvedTo: filterId, filterId });
  }

  const unresolved: FilterResolutionDigest['unresolved'] = [];
  for (const unknown of response.unknownFilters ?? []) {
    const requested =
      typeof (unknown as Row).name === 'string'
        ? String((unknown as Row).name)
        : JSON.stringify(unknown);
    unresolved.push({ requested, reason: 'unsupported filter' });
  }
  for (const invalid of response.relevantPromptText.invalid ?? []) {
    if (!unresolved.some((entry) => entry.requested === invalid)) {
      unresolved.push({ requested: invalid });
    }
  }

  return { kind: 'filter_resolution', applied, unresolved, targetPage };
}

/**
 * Named list fields on `company_info_selectors` whose resolved entries carry a
 * `.name` string we surface in APPLIED. The `filterId` is the field name.
 */
const COMPANY_INFO_NAME_FIELDS = [
  'competitors_of',
  'hq_geography',
  'funding_stage',
  'founder_prev_company',
  'founder_prev_university',
  'founder_gender',
  'founder_ethnicity',
] as const;

/** Segment metric keys present in `CompanyDiscoveryAiOutput.filters.segments`. */
const SEGMENT_METRIC_KEYS = [
  'headcount',
  'inflow',
  'outflow',
  'hiring',
  'attrition',
  'growth',
  'growth_yoy',
  'salary',
  'tenure',
] as const;

/**
 * Build a `filter_resolution` digest from a screener `CompanyDiscoveryAiOutput`.
 *
 * Screener applies navigate + apply real filters but the artifact has no root
 * `relevantPromptText`, so the TD `buildFilterResolutionDigest` can't read it.
 * This mirrors the BE summary in
 * `apps/dash-meta/.../turn-server-tools.summaries.ts` (collectScreenerApplied)
 * so FE and BE report the same filters. Sources:
 *
 * 1. `filters.primary_selector.{industry,geography,role}[].name`
 *    → filterId 'industry' | 'geography' | 'role'
 * 2. `filters.entity_info_selectors.company_info_selectors.<field>[].name`
 *    for name-bearing list fields → filterId = the field name
 * 3. `filters.segments.<metric>[].relevantPromptText` → captures superlative
 *    phrases like "highest attrition"; filterId = the metric key
 * 4. `unknownFilters[].name` → unresolved ('unsupported filter')
 */
export function buildScreenerFilterResolutionDigest(params: {
  response: CompanyDiscoveryAiOutput;
  targetPage: string;
}): FilterResolutionDigest {
  const { response, targetPage } = params;
  const filters = response.filters;
  const applied: FilterResolutionDigest['applied'] = [];

  // Every field below is `... | null` on the real CompanyDiscoveryAiOutput, and
  // `name` / `relevantPromptText` can be absent on a resolved item (e.g. a
  // numeric-bound segment carries start/end values but no prompt phrase). Guard
  // every string access so a partial screener response never throws.
  const pushName = (name: unknown, filterId: string): void => {
    if (typeof name === 'string' && name.length > 0) {
      applied.push({ requested: name, resolvedTo: name, filterId });
    }
  };

  // 1. primary_selector.{industry,geography,role}
  const primarySelector = filters?.primary_selector;
  if (primarySelector) {
    for (const key of ['industry', 'geography', 'role'] as const) {
      for (const item of primarySelector[key] ?? []) {
        pushName(item?.name, key);
      }
    }
  }

  // 2. entity_info_selectors.company_info_selectors.<field>
  const companyInfoSelectors =
    filters?.entity_info_selectors?.company_info_selectors;
  if (companyInfoSelectors) {
    for (const field of COMPANY_INFO_NAME_FIELDS) {
      for (const item of companyInfoSelectors[field] ?? []) {
        pushName(item?.name, field);
      }
    }
  }

  // 3. segments.<metric>[]: prefer the resolved phrase (superlatives like
  // "highest attrition"); otherwise synthesize a label from the numeric bounds
  // (e.g. "attrition ≥ 80") so an applied threshold is still reported.
  const segments = filters?.segments;
  if (segments) {
    for (const metric of SEGMENT_METRIC_KEYS) {
      for (const item of segments[metric] ?? []) {
        const text = item?.relevantPromptText;
        if (typeof text === 'string' && text.length > 0) {
          pushName(text, metric);
          continue;
        }
        const boundParts: string[] = [];
        if (item?.start_value != null) {
          boundParts.push(`≥ ${item.start_value}`);
        }
        if (item?.end_value != null) {
          boundParts.push(`≤ ${item.end_value}`);
        }
        if (boundParts.length > 0) {
          pushName(`${metric} ${boundParts.join(' and ')}`, metric);
        }
      }
    }
  }

  // 4. unknownFilters[].name → unresolved
  const unresolved: FilterResolutionDigest['unresolved'] = [];
  for (const unknown of response.unknownFilters ?? []) {
    if (typeof unknown?.name === 'string' && unknown.name.length > 0) {
      unresolved.push({
        requested: unknown.name,
        reason: 'unsupported filter',
      });
    }
  }

  return { kind: 'filter_resolution', applied, unresolved, targetPage };
}

export function buildReportPrefillDigest(params: {
  reportName: string;
  suggestions: GlobalAssistantReportParamSuggestion[];
}): ReportPrefillDigest {
  const resolved: ReportPrefillDigest['resolved'] = [];
  const skipped: ReportPrefillDigest['skipped'] = [];
  for (const suggestion of params.suggestions) {
    if (
      suggestion.resolutionStatus === 'resolved' &&
      suggestion.resolvedSelection
    ) {
      resolved.push({
        parameter: suggestion.displayName,
        value: suggestion.resolvedSelection.name,
      });
    } else {
      skipped.push({
        parameter: suggestion.displayName,
        searchTerm: suggestion.searchTerm,
        reason: suggestion.resolutionReason ?? suggestion.resolutionStatus,
      });
    }
  }
  return {
    kind: 'report_prefill',
    reportName: params.reportName,
    resolved,
    skipped,
  };
}

/**
 * Summarize the demographic/entity filters a chart was ACTUALLY rendered with
 * into stable labels, reusing the same applied-filter extraction the filter
 * digest does (companyNames/schoolNames + the dimensions in the `expr` tree).
 * Company/school carry their resolved name (`company: Google LLC`); every other
 * dimension is reported by its bare id (`gender`, `ethnicity`, …) because the
 * V2 response keeps no per-dimension human name. Returns `[]` for an absent
 * payload. Surfaced into the chart digest so the grounded narration has GROUND
 * TRUTH that the filters applied and never claims one "did not resolve"
 * (FE-2117).
 */
export function summarizeAppliedChartFilters(
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined
): string[] {
  if (filters === undefined) return [];
  const { applied } = buildFilterResolutionDigest({
    response: filters,
    targetPage: 'chart',
  });
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const entry of applied) {
    const label =
      entry.filterId === 'company' || entry.filterId === 'school'
        ? `${entry.filterId}: ${entry.resolvedTo}`
        : entry.filterId;
    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels;
}

/**
 * A successfully-settled chart digest carries only the raw `metric` (e.g.
 * "headcount") with no record of the FILTER scope the chart was rendered with
 * (e.g. "Asian men at Google"). Narrating from that bare digest makes the model
 * describe the value as unfiltered and redundantly offer a filter that is
 * already applied on the chart (FE-2117). Carry the model-facing chart
 * description (intentSummary, else chartSubtitle) into the digest `title` AND
 * the explicit applied-filter labels into `filtersApplied`, so the grounded
 * narration knows the chart's scope and has structured proof the filters
 * applied. An existing title / filtersApplied is preserved.
 */
export function withChartScope(
  digest: ChartDataDigest,
  scope: {
    intentSummary?: string;
    chartSubtitle?: string;
    filtersApplied?: string[];
  }
): ChartDataDigest {
  const title = digest.title ?? scope.intentSummary ?? scope.chartSubtitle;
  const filtersApplied =
    digest.filtersApplied ??
    (scope.filtersApplied && scope.filtersApplied.length > 0
      ? scope.filtersApplied
      : undefined);
  return {
    ...digest,
    ...(title ? { title } : {}),
    ...(filtersApplied ? { filtersApplied } : {}),
  };
}
